function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function toISODateString(year: number, month: number, day: number): string {
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

/**
 * Returns the first and last day of a given month as YYYY-MM-DD strings.
 * Month is 1-indexed (1 = January, 12 = December).
 */
export function getMonthDateRange(year: number, month: number): { startDate: string; endDate: string } {
  const lastDay = new Date(year, month, 0).getDate();
  return {
    startDate: toISODateString(year, month, 1),
    endDate: toISODateString(year, month, lastDay),
  };
}

/**
 * Returns the first and last day of a given year as YYYY-MM-DD strings.
 */
export function getYearDateRange(year: number): { startDate: string; endDate: string } {
  return {
    startDate: `${year}-01-01`,
    endDate: `${year}-12-31`,
  };
}

const LOCAL_DATE_STRING_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * The local calendar day of `date` as YYYY-MM-DD. Unlike `getISODateString`, which takes the UTC
 * day, this matches the day a (local-timezone) date picker shows.
 */
export function toLocalDateString(date: Date): string {
  return toISODateString(date.getFullYear(), date.getMonth() + 1, date.getDate());
}

/**
 * Local midnight of a YYYY-MM-DD string, or `null` when it is not a real calendar day (wrong
 * shape, or a rollover such as 2026-02-31). The inverse of `toLocalDateString`.
 */
export function parseLocalDateString(value: string | null | undefined): Date | null {
  const match = value ? LOCAL_DATE_STRING_PATTERN.exec(value) : null;
  if (!match) return null;

  const [, year, month, day] = match.map(Number);
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day ? date : null;
}

/** Local midnight on the Sunday that began the previous week. */
export function getStartOfPreviousWeek(now: Date = new Date()): Date {
  return new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay() - 7);
}
